import { useMutation } from "@vue/apollo-composable";
import { useI18n } from "vue-i18n";
import {
  ExperienceType,
  UpsertUserQuestionAnswerDocument,
} from "@/generated/graphql";
import { useAwaitAction } from "@/composables/use-await-action";
import { useGatingForAction } from "@/features/gating/evaluation/use-gating-for-action";
import { ChannelPermission } from "@f3/shared/permissions";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useTrackingStore } from "@/stores/tracking.store";
import { ExperienceAction } from "@/enums/experience-type.enum";
import { useToastStore } from "@/stores/toast.store";
import { useQuizCardState } from "./quiz-card-state";

/**
 * Single source of truth for submitting a channel quiz answer (gating,
 * mutation, failure toast, tracking). Requires an ancestor that provides the
 * quiz card state.
 */
export function useQuizVote(onSubmitted: () => void) {
  const quizCardState = useQuizCardState();
  const trackingService = useTrackingStore();
  const toastStore = useToastStore();
  const { t } = useI18n();

  const { mutate: upsertUserQuestionAnswer } = useMutation(
    UpsertUserQuestionAnswerDocument,
  );

  const { handler: vote, loading: voting } = useAwaitAction(
    async (optionId: string) => {
      const result = await upsertUserQuestionAnswer({
        userResponseData: {
          option_id: optionId,
        },
      });

      if (result?.data?.upsertUserQuestionAnswer !== true) {
        // The backend returns `false` (benign, no error) when the answer can't
        // be recorded — e.g. the quiz already ended or a stale race. Surface a
        // toast so the rejection is never silent.
        toastStore.addToast({
          message: t("channel.contests.quiz.answerFailed"),
          type: "error",
        });
        return;
      }

      trackingService.track({
        event: TrackEventEnum.ChannelQuizAnswered,
        category: EventCategory.ChannelQuiz,
        metadata: {
          quizId: quizCardState.quiz?.id,
          questionId: quizCardState.currentQuestion?.id,
          questionLabel: quizCardState.currentQuestion?.title,
          answerId: optionId,
          answerLabel: quizCardState.currentQuestion?.options?.find(
            (o) => o.id === optionId,
          )?.label,
          experienceId: quizCardState.quiz?.id,
          experienceType: ExperienceType.Quiz,
          experienceAction: ExperienceAction.QUIZ_ANSWER,
          experienceCreatorId: quizCardState.quiz?.created_by?.id ?? undefined,
          experienceCreatorUsername:
            quizCardState.quiz?.created_by?.username ?? undefined,
          experienceCommunityIds: quizCardState.quiz?.quiz_communities
            ?.map((c) => c.community?.id)
            .filter((id): id is string => id != null),
          experienceCommunityNames: quizCardState.quiz?.quiz_communities
            ?.map((c) => c.community?.name)
            .filter((name): name is string => name != null),
        },
      });
      quizCardState.lastVotedQuestionId =
        quizCardState.currentQuestion?.id ?? null;
      quizCardState.selectedOptionId = null;
      onSubmitted();
    },
  );

  const { handler: submitAnswer } = useGatingForAction(
    ChannelPermission.interactInContests,
    vote,
    {
      gatingExperience: {
        experienceId: quizCardState.quiz?.id,
        experienceType: ExperienceType.Quiz,
        experienceAction: ExperienceAction.QUIZ_ANSWER,
        experienceCreatorId: quizCardState.quiz?.created_by?.id ?? undefined,
        experienceCreatorUsername:
          quizCardState.quiz?.created_by?.username ?? undefined,
        experienceCommunityIds: quizCardState.quiz?.quiz_communities
          ?.map((c) => c.community?.id)
          .filter((id): id is string => id != null),
        experienceCommunityNames: quizCardState.quiz?.quiz_communities
          ?.map((c) => c.community?.name)
          .filter((name): name is string => name != null),
      },
    },
  );

  return { submitAnswer, voting };
}
